import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Pizza, Utensils, Layers } from "lucide-react";

interface ComboSaleItem {
  comboId: string;
  comboName: string;
  comboType: string;
  quantity: number;
  totalRevenue: number;
  originalPrice: number;
  comboPrice: number;
  totalSavings: number;
  averageSavings: number;
}

interface TopCombosTableProps {
  data: ComboSaleItem[];
}

const comboTypeConfig: Record<string, { label: string; icon: React.ComponentType<any>; variant: "default" | "secondary" | "outline" }> = {
  pizza: { label: "Pizza", icon: Pizza, variant: "default" },
  rodizio: { label: "Rodízio", icon: Utensils, variant: "secondary" },
  normal: { label: "Normal", icon: Layers, variant: "outline" },
};

export function TopCombosTable({ data }: TopCombosTableProps) {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  const formatPercent = (savings: number, original: number) => {
    if (original <= 0) return "0%";
    const percent = (savings / original) * 100;
    return `${percent.toFixed(1)}%`;
  };

  if (data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Combos Mais Vendidos</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[200px] flex items-center justify-center text-muted-foreground">
            Nenhum combo vendido no período selecionado
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Combos Mais Vendidos</CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[50px]">#</TableHead>
              <TableHead>Combo</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead className="text-right">Qtd</TableHead>
              <TableHead className="text-right">Receita</TableHead>
              <TableHead className="text-right">Economia Média</TableHead>
              <TableHead className="text-right">Economia Total</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.slice(0, 10).map((combo, index) => {
              const typeConfig = comboTypeConfig[combo.comboType] || comboTypeConfig.normal;
              const IconComponent = typeConfig.icon;

              return (
                <TableRow key={combo.comboId}>
                  <TableCell className="font-medium">{index + 1}</TableCell>
                  <TableCell className="font-medium">{combo.comboName}</TableCell>
                  <TableCell>
                    <Badge variant={typeConfig.variant} className="gap-1">
                      <IconComponent className="h-3 w-3" />
                      {typeConfig.label}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">{combo.quantity}</TableCell>
                  <TableCell className="text-right">{formatCurrency(combo.totalRevenue)}</TableCell>
                  <TableCell className="text-right">
                    <span className="text-green-600 dark:text-green-400">
                      {formatCurrency(combo.averageSavings)}
                    </span>
                    <span className="text-xs text-muted-foreground ml-1">
                      ({formatPercent(combo.averageSavings, combo.originalPrice)})
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <span className="text-green-600 dark:text-green-400 font-medium">
                      {formatCurrency(combo.totalSavings)}
                    </span>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
